import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { getCampaign } from "../lib/gtm";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = logged out, object = authenticated
  const [user, setUser] = useState(null);
  const userId = user?.user_id;

  useEffect(() => {
    if (!userId || user?.role === "admin") return;
    const campaign = getCampaign();
    const flag = `cck_attr_sent_${userId}`;
    if (!Object.keys(campaign).length || sessionStorage.getItem(flag)) return;
    sessionStorage.setItem(flag, "1");
    api.post("/auth/attribution", { campaign }).catch(() => {});
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Global payments switch (Admin → Settings). null = loading.
  const [paymentsEnabled, setPaymentsEnabled] = useState(null);
  const [freeUntil, setFreeUntil] = useState(null);

  useEffect(() => {
    api.get("/settings")
      .then((r) => { setPaymentsEnabled(r.data?.payments_enabled !== false); setFreeUntil(r.data?.free_until || null); })
      .catch(() => setPaymentsEnabled(true));
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    // CRITICAL: On the /complete-payment landing (approval-email payment link),
    // skip the initial /auth/me so it can't race the magic-login POST. Without
    // this, a 401 from /auth/me after cookies are set clobbers setUser(signedIn)
    // and the user is bounced to /login instead of landing on the plan card.
    if (window.location.pathname === "/complete-payment") {
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, logout, refresh: checkAuth, paymentsEnabled, freeUntil }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Central onboarding state-machine used by dashboardFor + Protected route guard.
// Each role progresses: verify email → complete profile → (admin approval) → pay → dashboard.
//
// Invited users (users with `invite_token` on their doc) replace the regular
// `/subscribe` step with `/invite-payment-plan` — a dedicated confirmation
// page showing the 3-tier pricing timeline (3 months free → R149 × 12
// months → R299 standard) with a terms checkbox. Clicking "Subscribe Now"
// there kicks off the existing /invite-trial/initialize endpoint and takes
// them to Paystack. Once `invite_trial_captured_at` is set the user doc's
// `subscription_status` is `active`, so dashboardFor drops back into the
// normal portal routes below.
const needsInviteTrialCapture = (user) =>
  !!user?.invite_token && !user?.invite_trial_captured_at && !user?.payments_disabled;

export const dashboardFor = (user) => {
  if (!user) return "/login";
  if (user.role === "admin") return "/admin";

  // Bundle Deal members share a single bundle subscription that covers all
  // four role profiles. A per-role `subscription_status` flag on their user
  // doc can go stale (e.g. after a sync_user_subscription run keyed to a
  // non-primary user_id). Treat bundle_member users as active for routing
  // so they are never bounced to /subscribe or /checkout from any of their
  // role dashboards. Parent flow is unchanged — bundle parents already
  // land on /dashboard the normal way.
  const isActive = user.subscription_status === "active" || !!user.bundle_member;

  if (user.role === "parent") {
    if (!user.profile_completed) return "/profile-setup";
    return "/dashboard";
  }
  if (user.role === "vendor") {
    if (!user.profile_completed) return "/vendor-portal";       // setup step
    if (user.vendor_status !== "approved") return "/vendor-portal"; // awaits admin approval
    if (needsInviteTrialCapture(user)) return "/invite-payment-plan";
    if (!isActive) return "/checkout?plan=vendor";
    return "/vendor-portal";
  }
  if (user.role === "event_creator") {
    if (!user.profile_completed) return "/event-creator-setup";
    if (user.vendor_status !== "approved") return "/event-creator-setup";
    if (needsInviteTrialCapture(user)) return "/invite-payment-plan";
    if (!isActive) return "/checkout?plan=eventcreator";
    return "/event-creator-portal";
  }
  if (user.role === "marketplace_user") {
    if (!user.profile_completed) return "/marketplace-setup";
    if (user.vendor_status !== "approved") return "/marketplace-setup";
    if (needsInviteTrialCapture(user)) return "/invite-payment-plan";
    if (!isActive) return "/checkout?plan=marketplace";
    return "/marketplace-portal";
  }
  return "/subscribe";
};
